import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { normalizePhone, PHONE_REGEX, PHONE_HELP } from "@/lib/phone";
import {
  useCreateAccount,
  useUpdateAccount,
  useListAccountContacts,
  useUpdateAccountContact,
  useCreateAccountContact,
  getListAccountsQueryKey,
  getGetAccountQueryKey,
  getListAccountContactsQueryKey,
  type Account,
  type Contact,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

/**
 * 2026-06-10 — Edit Account dialog rework.
 *
 * Operator UAT: the prior dialog only showed account-level fields
 * (legal name, kind, type, GST, place of supply, billing email/phone/
 * address, price mode). Convert-Lead asks for a much fuller set —
 * personal details on the primary contact + extended company details
 * with pincode/city/state/companyType + ID proof type/number.
 *
 * The Edit dialog now mirrors the Convert-Lead form's section layout:
 *   • Personal Details accordion (binds to the primary contact row):
 *       First Name, Last Name, Mobile Number, Email,
 *       Personal ID Type, ID Number
 *   • "Are you a company?" toggle
 *   • Company Details accordion (binds to the account row, shown when
 *     toggle is on):
 *       Company Legal Name, Company Type, GST Treatment, GSTIN (B2B),
 *       Company Address, Pincode, City, State, Place of Supply
 *
 * On submit:
 *   • PATCH /accounts/:id with the changed account fields
 *   • PATCH /accounts/:id/contacts/:primaryId with the changed contact
 *     fields. If no primary contact exists yet (rare; convert always
 *     creates one), POST a new primary contact instead.
 *
 * Document upload affordances are NOT in this dialog — they live on
 * the Documents tab on /accounts/:id (operators can re-upload there).
 * priceModeDefault is dropped from the visible UI since Convert-Lead
 * doesn't ask for it either; server keeps existing value.
 */

// 2026-06-10 — the contact-update route's UpdateContactBodyIdProofType enum
// is narrower than convert-lead's (Aadhaar | PAN | Passport | DL only — no
// Driving_License, no Voter_ID). Until those two server enums are reconciled,
// the Edit form sticks to the contact-route subset so PATCH /contacts/:id
// can accept whatever the operator picks.
const ID_TYPES = ["Aadhaar", "PAN", "Passport", "DL"] as const;
const ID_TYPE_LABELS: Record<(typeof ID_TYPES)[number], string> = {
  Aadhaar: "Aadhaar",
  PAN: "PAN",
  Passport: "Passport",
  DL: "Driving License",
};

const COMPANY_TYPES = [
  "Private_Limited",
  "Public_Limited",
  "LLP",
  "Partnership",
  "Sole_Proprietorship",
  "One_Person_Company",
  "Other",
] as const;
const COMPANY_TYPE_LABELS: Record<(typeof COMPANY_TYPES)[number], string> = {
  Private_Limited: "Private Limited",
  Public_Limited: "Public Limited",
  LLP: "LLP",
  Partnership: "Partnership",
  Sole_Proprietorship: "Sole Proprietorship",
  One_Person_Company: "One Person Company",
  Other: "Other",
};

const AccountFormSchema = z.object({
  // Personal Details — bound to the primary contact row
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().nullable().optional(),
  mobile: z.string().regex(PHONE_REGEX, PHONE_HELP),
  email: z.string().email("Invalid email").nullable().optional().or(z.literal("")),
  idProofType: z.enum(ID_TYPES).nullable().optional(),
  idProofRef: z.string().nullable().optional(),

  // Are you a company?
  isCompany: z.boolean(),

  // Account / company fields
  legalName: z.string().min(1, "Legal name is required"),
  companyType: z.enum(COMPANY_TYPES).nullable().optional(),
  gstTreatment: z.enum(["registered_b2b", "unregistered_b2c"]),
  gstin: z.string().nullable().optional(),
  placeOfSupply: z.string().min(1, "Place of supply is required"),
  billingAddress: z.string().nullable().optional(),
  pincode: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
});

type AccountFormValues = z.infer<typeof AccountFormSchema>;

interface Props {
  open: boolean;
  account?: Account | null;
  onClose: () => void;
}

// Split a full name into (first, last). Single token → first only.
function splitName(full: string | null | undefined): { first: string; last: string } {
  const trimmed = (full ?? "").trim();
  if (!trimmed) return { first: "", last: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { first: parts[0]!, last: "" };
  return { first: parts[0]!, last: parts.slice(1).join(" ") };
}

export function AccountForm({ open, account, onClose }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const updateContact = useUpdateAccountContact();
  const createContact = useCreateAccountContact();

  // Load contacts for an existing account so we can find the primary one.
  // Enabled only when we have an id to query (i.e. edit mode + open).
  const contactsQuery = useListAccountContacts(account?.id ?? 0, {
    query: {
      enabled: open && account != null,
      queryKey: getListAccountContactsQueryKey(account?.id ?? 0),
    },
  });
  const primaryContact = useMemo<Contact | null>(() => {
    const rows = (contactsQuery.data ?? []) as Contact[];
    const primary = rows.find((c) => Array.isArray(c.roles) && c.roles.includes("primary"));
    return primary ?? rows[0] ?? null;
  }, [contactsQuery.data]);

  const { first: initFirst, last: initLast } = splitName(primaryContact?.fullName);
  const initIsCompany = (account?.type ?? "individual") === "company";

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(AccountFormSchema),
    defaultValues: {
      firstName: initFirst,
      lastName: initLast,
      mobile: normalizePhone(primaryContact?.phone ?? account?.billingPhone ?? ""),
      email: primaryContact?.email ?? account?.billingEmail ?? "",
      idProofType: (primaryContact?.idProofType ?? null) as AccountFormValues["idProofType"],
      idProofRef: primaryContact?.idProofRef ?? null,
      isCompany: initIsCompany,
      legalName: account?.legalName ?? "",
      companyType: (account?.companyType ?? null) as AccountFormValues["companyType"],
      gstTreatment: account?.gstTreatment ?? "unregistered_b2c",
      gstin: account?.gstin ?? null,
      placeOfSupply: account?.placeOfSupply ?? "07",
      billingAddress: account?.billingAddress ?? null,
      pincode: account?.pincode ?? null,
      city: account?.city ?? null,
      state: account?.state ?? null,
    },
  });

  // Re-hydrate form defaults once contacts arrive (initial render fires before
  // useListAccountContacts resolves, so the primary contact fields would be
  // empty without this).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (!open) {
      setHydrated(false);
      return;
    }
    if (hydrated) return;
    if (contactsQuery.isLoading) return;
    const { first, last } = splitName(primaryContact?.fullName);
    form.reset({
      firstName: first,
      lastName: last,
      mobile: normalizePhone(primaryContact?.phone ?? account?.billingPhone ?? ""),
      email: primaryContact?.email ?? account?.billingEmail ?? "",
      idProofType: (primaryContact?.idProofType ?? null) as AccountFormValues["idProofType"],
      idProofRef: primaryContact?.idProofRef ?? null,
      isCompany: (account?.type ?? "individual") === "company",
      legalName: account?.legalName ?? "",
      companyType: (account?.companyType ?? null) as AccountFormValues["companyType"],
      gstTreatment: account?.gstTreatment ?? "unregistered_b2c",
      gstin: account?.gstin ?? null,
      placeOfSupply: account?.placeOfSupply ?? "07",
      billingAddress: account?.billingAddress ?? null,
      pincode: account?.pincode ?? null,
      city: account?.city ?? null,
      state: account?.state ?? null,
    });
    setHydrated(true);
  }, [open, hydrated, contactsQuery.isLoading, primaryContact, account, form]);

  const watchedGstTreatment = form.watch("gstTreatment");
  const isB2B = watchedGstTreatment === "registered_b2b";
  const watchedIsCompany = form.watch("isCompany");

  async function onSubmit(values: AccountFormValues) {
    try {
      // 1) Account body
      const accountBody = {
        legalName: values.legalName,
        type: values.isCompany ? ("company" as const) : ("individual" as const),
        companyType: values.isCompany ? (values.companyType ?? null) : null,
        gstTreatment: values.gstTreatment,
        gstin: isB2B ? (values.gstin || null) : null,
        placeOfSupply: values.placeOfSupply,
        billingAddress: values.billingAddress || null,
        pincode: values.pincode || null,
        city: values.city || null,
        state: values.state || null,
        billingEmail: values.email || null,
        billingPhone: values.mobile || null,
      };

      const fullName = `${values.firstName} ${values.lastName ?? ""}`.trim();

      if (account) {
        // EDIT mode
        await updateAccount.mutateAsync({ id: account.id, data: accountBody });

        // Contact upsert
        if (primaryContact) {
          await updateContact.mutateAsync({
            id: account.id,
            contactId: primaryContact.id,
            data: {
              fullName,
              phone: values.mobile,
              email: values.email || null,
              idProofType: values.idProofType ?? null,
              idProofRef: values.idProofRef || null,
            },
          });
        } else {
          // No primary contact existed yet — create one. This covers the
          // unusual case of an account that was never created via convert.
          await createContact.mutateAsync({
            id: account.id,
            data: {
              fullName,
              phone: values.mobile,
              email: values.email || null,
              roles: ["primary", "user"],
              idProofType: values.idProofType ?? null,
              idProofRef: values.idProofRef || null,
            },
          });
        }

        toast({ title: "Account updated." });
        queryClient.invalidateQueries({ queryKey: getGetAccountQueryKey(account.id) });
        queryClient.invalidateQueries({
          queryKey: getListAccountContactsQueryKey(account.id),
        });
      } else {
        // CREATE mode (kept for legacy completeness — Accounts module redesign
        // prefers Add Account → /accounts/convert/:leadId).
        await createAccount.mutateAsync({
          data: { ...accountBody, kind: "walk_in" as const },
        });
        toast({ title: "Account created." });
        queryClient.invalidateQueries({ queryKey: getListAccountsQueryKey() });
      }
      onClose();
      form.reset();
    } catch (err) {
      const detail = (err as Error)?.message ?? "Unknown error";
      toast({
        title: "Save failed.",
        description: detail,
        variant: "destructive",
      });
    }
  }

  const isPending =
    createAccount.isPending ||
    updateAccount.isPending ||
    updateContact.isPending ||
    createContact.isPending;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{account ? "Edit Account" : "New Account"}</SheetTitle>
          <SheetDescription>
            {account
              ? "Update personal and company details for this account."
              : "Create a new account with personal and (optionally) company details."}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Accordion
              type="multiple"
              defaultValue={["personal", "company"]}
              className="flex flex-col gap-2"
            >
              {/* ============== Personal Details ============== */}
              <AccordionItem value="personal" className="border rounded-md px-3">
                <AccordionTrigger className="hover:no-underline py-3">
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Personal Details
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-1 pb-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField name="firstName" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="lastName" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <FormField name="mobile" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile Number <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          placeholder="10-digit mobile (no +91, no spaces)"
                          maxLength={10}
                          {...field}
                          onChange={(e) => field.onChange(normalizePhone(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField name="email" control={form.control} render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl><Input type="email" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <div className="grid grid-cols-2 gap-3">
                    <FormField name="idProofType" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Personal ID Type</FormLabel>
                        <Select
                          onValueChange={(v) => field.onChange(v === "none" ? null : v)}
                          value={field.value ?? "none"}
                        >
                          <FormControl><SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {ID_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>{ID_TYPE_LABELS[t]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField name="idProofRef" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>ID Number</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ""} placeholder="e.g. ABCDE1234F" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* ============== Are you a company? toggle ============== */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Are you a company?</Label>
                <p className="text-[10px] text-muted-foreground">
                  Toggle on to bill a company entity (B2B GSTIN, separate legal name).
                </p>
              </div>
              <Switch
                checked={watchedIsCompany}
                onCheckedChange={(v) => form.setValue("isCompany", v)}
              />
            </div>

            {watchedIsCompany && (
              <Accordion
                type="multiple"
                defaultValue={["company"]}
                className="flex flex-col gap-2"
              >
                <AccordionItem value="company" className="border rounded-md px-3">
                  <AccordionTrigger className="hover:no-underline py-3">
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Company Details
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3 pt-1 pb-3">
                    <FormField name="legalName" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Legal Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl><Input {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField name="companyType" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Type</FormLabel>
                        <Select
                          onValueChange={(v) => field.onChange(v === "none" ? null : v)}
                          value={field.value ?? "none"}
                        >
                          <FormControl><SelectTrigger><SelectValue placeholder="Choose…" /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            {COMPANY_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>{COMPANY_TYPE_LABELS[t]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <FormField name="gstTreatment" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>GST Treatment</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="unregistered_b2c">Not Registered</SelectItem>
                            <SelectItem value="registered_b2b">GST Registered</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />

                    {isB2B && (
                      <FormField name="gstin" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>GSTIN <span className="text-destructive">*</span></FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value ?? ""} placeholder="22AAAAA0000A1Z5" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    )}

                    <FormField name="billingAddress" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Address</FormLabel>
                        <FormControl>
                          <Textarea {...field} value={field.value ?? ""} rows={2} placeholder="Street, Area" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <div className="grid grid-cols-3 gap-3">
                      <FormField name="pincode" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pincode</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField name="city" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField name="state" control={form.control} render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <FormField name="placeOfSupply" control={form.control} render={({ field }) => (
                      <FormItem>
                        <FormLabel>Place of Supply <span className="text-destructive">*</span></FormLabel>
                        <FormControl><Input {...field} placeholder="07" /></FormControl>
                        <p className="text-xs text-muted-foreground">
                          2-digit GST state code (e.g. '07' Delhi, '06' Haryana, '27' Maharashtra)
                        </p>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {/* When NOT a company, we still need legalName + GST treatment +
                place of supply on the account row. Mirror the individual-mode
                Convert-Lead approach: legalName mirrors first+last; GST + PoS
                surface here in a compact row. */}
            {!watchedIsCompany && (
              <div className="space-y-3 rounded-md border p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Billing
                </p>
                <FormField name="gstTreatment" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>GST Treatment</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="unregistered_b2c">Not Registered</SelectItem>
                        <SelectItem value="registered_b2b">GST Registered</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="placeOfSupply" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Place of Supply <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input {...field} placeholder="07" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="billingAddress" control={form.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Address</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value ?? ""} rows={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving…" : account ? "Save Changes" : "Create Account"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
